import { Box, Text, CloseIconUI } from '@instamenu/ui-system-next';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { formatCurrency } from '@instamenu/formatters';
import { observer } from 'mobx-react-lite';
import { useState, useEffect, useCallback } from 'react';
import {
  buttonStyles,
  menuContainer,
  menuContainerResponsive,
  menuItems,
  scrollMenu,
} from './cart-menu.css';
import { useDismissible } from '@instamenu/react-hooks';
import { useUIStateContext } from '../ui-state-context/ui-state-context';
import { ActionBar } from './action-bar';
import { Order, OrderItem, useCheckoutContext } from '@instamenu/context-checkout';
import { CartOrderItem } from './cart-order-item';

export type SideMenuBarProps = {
  order: Order;
  onUpdateItemQuantity?: (orderItem: OrderItem, quantity: number) => void;
  isDesktop?: boolean;
};

export const SideMenuBar = observer((props: SideMenuBarProps) => {
  const { order, isDesktop } = props;
  const { setIsCartOpen, isCartOpen } = useUIStateContext();
  const [isClosing, setIsClosing] = useState(false);

  const checkout = useCheckoutContext();

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsCartOpen(false);
      setIsClosing(false);
    }, 500);
  }, [setIsCartOpen]);

  const { ref: internalRef } = useDismissible<HTMLDivElement>(handleClose);

  useEffect(() => {
    if (order.orderItems.length === 0) {
      handleClose();
    }
  }, [isCartOpen, order.orderItems.length, handleClose]);

  if (!checkout) return null;

  return (
    <Box
      ref={internalRef}
      style={{ flex: isDesktop ? (order.orderItems.length > 0 ? 1.2 : 0) : '' }}
      className={`${isDesktop ? menuContainer : menuContainerResponsive} ${
        isClosing ? 'closing' : ''
      }`}
    >
      <Box className={scrollMenu}>
        {!isDesktop && (
          <Box>
            <CloseIconUI path={handleClose} />
          </Box>
        )}
        <Text variant="h2" css={{ padding: '12px 12px 0 12px' }}>
          Your Cart
        </Text>
        <Box className={menuItems}>
          {order.orderItems.map((orderItem, index) => (
            <CartOrderItem orderItem={orderItem} key={index} />
          ))}
        </Box>
        {order.restaurant.currentMenu.minGrossSubtotal > 0 &&
        order.netOrderItemsTotal < order.restaurant.currentMenu.minGrossSubtotal ? (
          <Text variant="error">
            The minimum order amount is{' '}
            {formatCurrency(order.restaurant.currentMenu.minGrossSubtotal)}.
          </Text>
        ) : null}
        {checkout.loyalty?.isActive &&
          checkout.loyalty.status &&
          checkout.loyalty.cartPoints > 0 && (
            <Box
              css={{
                margin: '0 12px 8px',
                padding: '10px 14px',
                borderRadius: 10,
                background: `color-mix(in srgb, ${themeVars.color.brand} 7%, transparent)`,
                border: `1px solid color-mix(in srgb, ${themeVars.color.brand} 19%, transparent)`,
                color: themeVars.color.brand,
                textAlign: 'center',
                fontSize: 13,
              }}
            >
              {checkout.loyalty.isItemBased
                ? `You'll earn ${checkout.loyalty.cartPoints} ${checkout.loyalty.cartPoints === 1 ? 'punch' : 'punches'} with this order`
                : `You'll earn ${checkout.loyalty.cartPoints.toLocaleString()} points with this order`}
            </Box>
          )}
        <Box className={buttonStyles}>
          <ActionBar buttonProps={{ css: { display: 'flex', flex: 1 } }} />
        </Box>
      </Box>
    </Box>
  );
});
